import { Theme, ThemeDocument, ThemeQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { computed, ref, watch } from "vue";
import { InfoTwinTheme } from "./theme.types";
import {
    convertToThemeColors,
    createThemeObject,
    getDefaultThemeColors,
    isThemeObject,
} from "@/shell/helpers/theme.helpers";
import { useTenant } from "@/shared/composables/tenant";
import { publicClient } from "@/shared/services/apollo-clients/public-client.ts";
import { getDataQueryLanguages } from "@/shared/services/providers/language-provider.ts";

const themeDefinitions = ref<InfoTwinTheme>();
const themeResult = ref<ThemeQuery>();

const colors = computed(() => {
    if (themeDefinitions.value?.colors) return themeDefinitions.value?.colors;

    return getDefaultThemeColors();
});

export const useTheme = () => {
    const { tenant } = useTenant();

    watch(
        () => tenant.value,
        async () => {
            if (themeResult.value) return;

            const { data, errors } = await publicClient.query<ThemeQuery>({
                query: ThemeDocument,
                variables: { acceptedLanguages: getDataQueryLanguages() },
            });

            if (errors) {
                console.error(errors);
                return;
            }

            themeResult.value = data;
        }
    );

    const themeLoading = ref<boolean>(true);

    const themeTitle = computed(() => {
        return themeResult.value?.theme?.teasers?.title
            ? themeResult.value.theme?.teasers?.title
            : (tenant.value?.displayName ?? "");
    });

    watch(themeResult, () => {
        if (!themeResult.value || themeDefinitions.value) return;

        themeDefinitions.value = createThemeObject(themeResult.value, themeTitle.value);
        themeLoading.value = false;
    });

    /**
     * Changes the InfoTwin theme temporarily for the Branding preview
     * @param theme Theme from message event.
     */
    function changeTheme(theme: Theme) {
        if (!themeDefinitions.value) return;

        const themeDefinitionCopy = { ...themeDefinitions.value };

        themeDefinitionCopy.logoURL = theme.logo?.blobId
            ? `${window.location.origin}/downloads/blobs/${theme.logo?.blobId}`
            : "";
        themeDefinitionCopy.logoPath = theme.logo?.path ? theme?.logo?.path : "";
        themeDefinitionCopy.showTitle = theme.showTitle ?? true;
        themeDefinitionCopy.logoPath = theme.logo?.path ?? "";
        themeDefinitionCopy.colors = convertToThemeColors(theme.theming?.colors);
        themeDefinitionCopy.title = theme.teasers?.title ? theme.teasers.title : (tenant.value?.displayName ?? "");
        themeDefinitions.value = themeDefinitionCopy;
    }

    /**
     * Receives the message from the event listener, only activates if it comes from the same origin
     * and filters the message, to check if its a ThemeObject and not something else.
     * @param event Event data from the message event listener.
     * @returns
     */
    function receiveMessage(event: MessageEvent<unknown>) {
        if (location.hostname !== "localhost" && event.origin !== window.location.origin) return;

        if (isThemeObject(event.data)) {
            changeTheme(event.data);
        }
    }

    return {
        themeDefinitions,
        receiveMessage,
        colors,
        themeLoading,
        themeTitle,
    };
};
